import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import type { Product } from "../../types/booking";
import {
  findDuplicateProductGroups,
  getDuplicateProductIds,
} from "../../utils/bookingProductDuplicates";
import { deleteBookingProducts } from "../../services/bookingService";

interface BookingProductDuplicateActionsProps {
  products: Product[];
  onRemoved: () => void;
}

export const BookingProductDuplicateActions: React.FC<
  BookingProductDuplicateActionsProps
> = ({ products, onRemoved }) => {
  const duplicateGroups = useMemo(
    () => findDuplicateProductGroups(products),
    [products]
  );
  const duplicateIdsToRemove = useMemo(
    () => getDuplicateProductIds(products, "oldest"),
    [products]
  );
  const [reviewOpen, setReviewOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  if (duplicateGroups.length === 0) {
    return null;
  }

  const duplicateCount = duplicateIdsToRemove.length;
  const reviewRows = duplicateGroups.flatMap((group) =>
    [...group.products]
      .sort((a, b) => a.id - b.id)
      .map((product, index) => ({
        ...product,
        groupKey: group.key,
        matchBy: group.matchBy,
        matchValue: group.matchValue,
        willRemove: index > 0,
      }))
  );

  const handleRemoveDuplicates = async (productIds: number[]) => {
    if (productIds.length === 0) return;

    setRemoving(true);
    try {
      const { success, error } = await deleteBookingProducts(productIds);
      if (error || !success) {
        message.error("Failed to remove duplicate products");
        return;
      }

      message.success(
        `Removed ${productIds.length} duplicate product${
          productIds.length === 1 ? "" : "s"
        }`
      );
      setReviewOpen(false);
      onRemoved();
    } catch (error) {
      console.error("Error removing duplicate products:", error);
      message.error("Failed to remove duplicate products");
    } finally {
      setRemoving(false);
    }
  };

  const reviewColumns: ColumnsType<(typeof reviewRows)[number]> = [
    {
      title: "Match",
      key: "match",
      width: 140,
      render: (_, record) => (
        <Tag color={record.matchBy === "ean" ? "blue" : "default"}>
          {record.matchBy === "ean" ? "EAN" : "Name"}
        </Tag>
      ),
    },
    {
      title: "Value",
      dataIndex: "matchValue",
      key: "matchValue",
      width: 160,
    },
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Pieces",
      dataIndex: "pieces",
      key: "pieces",
      width: 80,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) =>
        record.willRemove ? (
          <Tag color="red">Remove</Tag>
        ) : (
          <Tag color="green">Keep</Tag>
        ),
    },
  ];

  return (
    <>
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        style={{ marginBottom: 16 }}
        message={`${duplicateCount} duplicate product${
          duplicateCount === 1 ? "" : "s"
        } found across ${duplicateGroups.length} group${
          duplicateGroups.length === 1 ? "" : "s"
        }`}
        description={
          <Typography.Text type="secondary">
            Duplicates are matched by EAN when available, otherwise by product
            name. Product imports add rows without checking existing products.
          </Typography.Text>
        }
        action={
          <Space direction="vertical" size={4}>
            <Button
              danger
              size="small"
              loading={removing}
              onClick={() => handleRemoveDuplicates(duplicateIdsToRemove)}
            >
              Remove duplicates
            </Button>
            <Button size="small" onClick={() => setReviewOpen(true)}>
              Review
            </Button>
          </Space>
        }
      />

      <Modal
        title="Review duplicate products"
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        width={960}
        footer={[
          <Button key="cancel" onClick={() => setReviewOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="remove"
            type="primary"
            danger
            icon={<DeleteOutlined />}
            loading={removing}
            onClick={() => handleRemoveDuplicates(duplicateIdsToRemove)}
          >
            Remove {duplicateCount} duplicate{duplicateCount === 1 ? "" : "s"}
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary">
          The oldest row in each group is kept. All other rows will be deleted.
        </Typography.Paragraph>
        <Table
          columns={reviewColumns}
          dataSource={reviewRows}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: 420 }}
          onRow={(record) =>
            record.willRemove ? { style: { backgroundColor: "#fff7e6" } } : {}
          }
        />
      </Modal>
    </>
  );
};
